import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Minus, RefreshCw } from 'lucide-react';
import { DiffViewer } from '@/components/diff-viewer/DiffViewer';
import { DiffNavigation } from '@/components/diff-viewer/DiffNavigation';
import { CollapsibleSection } from '@/components/diff-viewer/CollapsibleSection';
import { DiffViewModeToggle } from '@/components/diff-viewer/DiffViewModeToggle';
import { useViewModePersistence } from '@/hooks/use-view-mode-persistence';

export function DiffViewerPage() {
  const [expectedJson, setExpectedJson] = useState('');
  const [actualJson, setActualJson] = useState('');
  const [parsedExpected, setParsedExpected] = useState<unknown | null>(null);
  const [parsedActual, setParsedActual] = useState<unknown | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [diffIndex, setDiffIndex] = useState(0);
  const [viewMode, setViewMode] = useViewModePersistence('split');

  const handleCompare = () => {
    try {
      const expected = JSON.parse(expectedJson);
      const actual = JSON.parse(actualJson);
      setParsedExpected(expected);
      setParsedActual(actual);
      setParseError(null);
      setDiffIndex(0);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : 'Invalid JSON');
      setParsedExpected(null);
      setParsedActual(null);
    }
  };

  const handleClear = () => {
    setExpectedJson('');
    setActualJson('');
    setParsedExpected(null);
    setParsedActual(null);
    setParseError(null);
    setDiffIndex(0);
  };

  // Compute diff stats
  const computeDiffStats = () => {
    if (!parsedExpected || !parsedActual) return null;

    // Simple heuristic: count fields that are different
    const expectedStr = JSON.stringify(parsedExpected, null, 2);
    const actualStr = JSON.stringify(parsedActual, null, 2);

    const expectedLines = expectedStr.split('\n');
    const actualLines = actualStr.split('\n');

    let added = 0;
    let removed = 0;
    let modified = 0;

    // Simple line-based comparison
    const maxLines = Math.max(expectedLines.length, actualLines.length);
    for (let i = 0; i < maxLines; i++) {
      const expLine = expectedLines[i] || '';
      const actLine = actualLines[i] || '';

      if (!expLine && actLine) added++;
      else if (expLine && !actLine) removed++;
      else if (expLine !== actLine) modified++;
    }

    return { added, removed, modified };
  };

  const diffStats = computeDiffStats();

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Diff Viewer</h1>
          <p className="text-muted-foreground mt-2">
            Compare expected (ground truth) JSON with actual (extracted) JSON to identify differences
          </p>
        </div>
        <DiffViewModeToggle value={viewMode} onChange={setViewMode} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Expected (Ground Truth)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={expectedJson}
              onChange={(e) => setExpectedJson(e.target.value)}
              placeholder='{"rates": [{"origin": "LAX", "destination": "NYC", "price": 100}]}'
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actual (Extracted)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={actualJson}
              onChange={(e) => setActualJson(e.target.value)}
              placeholder='{"rates": [{"origin": "LAX", "destination": "NYC", "price": 120}]}'
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2">
        <Button onClick={handleCompare} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Compare
        </Button>
        <Button onClick={handleClear} variant="outline" className="gap-2">
          Clear
        </Button>
      </div>

      {parseError && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Parse Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-destructive">{parseError}</p>
          </CardContent>
        </Card>
      )}

      {parsedExpected && parsedActual && !parseError && (
        <>
          {diffStats && (
            <Card>
              <CardHeader>
                <CardTitle>Diff Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="success" className="gap-1">
                      <Plus className="h-3 w-3" />
                      {diffStats.added} Added
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive" className="gap-1">
                      <Minus className="h-3 w-3" />
                      {diffStats.removed} Removed
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="warning" className="gap-1">
                      <RefreshCw className="h-3 w-3" />
                      {diffStats.modified} Modified
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <DiffNavigation
            currentIndex={diffIndex}
            total={diffStats ? diffStats.added + diffStats.removed + diffStats.modified : 1}
            onPrevious={() => setDiffIndex(Math.max(0, diffIndex - 1))}
            onNext={() => setDiffIndex(diffIndex + 1)}
          />

          <DiffViewer expected={parsedExpected} actual={parsedActual} viewMode={viewMode} />

          {/* Collapsible sections for specific data keys */}
          {typeof parsedActual === 'object' && parsedActual !== null && (
            <div className="space-y-2">
              <Card>
                <CardHeader>
                  <CardTitle>Expandable Sections</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {Object.entries(parsedActual as Record<string, unknown>).map(([key, value]) => (
                    <CollapsibleSection
                      key={key}
                      title={key}
                      data={value}
                      defaultOpen={key === 'rates' || key === 'surcharges'}
                      badge={
                        Array.isArray(value) ? (
                          <Badge variant="secondary">{value.length} items</Badge>
                        ) : null
                      }
                    />
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
